/**
 * Invoices Page
 * View and manage invoices
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SubscriptionLayout from '../../components/subscription/SubscriptionLayout';
import { useAuth } from '../../contexts/AuthContext';
import { FileText, Download, ExternalLink } from 'lucide-react';
import { getInvoices } from '../../services/SubscriptionService';

// Define Invoice interface locally to match SubscriptionService
interface Invoice {
  invoiceId: string;
  organizationId: string;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  periodStart?: string;
  periodEnd?: string;
  dueDate?: string;
  paidAt?: string;
  stripeInvoiceId?: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  stripeReceiptUrl?: string;
  stripeReceiptNumber?: string;
  stripeHostedInvoiceUrl?: string;
  stripeInvoicePdf?: string;
  lineItems: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
  }>;
  createdAt: string;
}

const InvoicesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const organizationId = searchParams.get('org') || user?.organizationId || null;

  useEffect(() => {
    if (organizationId) {
      loadInvoices();
    }
  }, [organizationId]);

  const loadInvoices = async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      const invs = await getInvoices(organizationId);
      setInvoices(invs);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SubscriptionLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <FileText className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <span>Invoices</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            View and download your invoices
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center border border-gray-100 dark:border-gray-700">
            <FileText className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No invoices found</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700">
            <div className="space-y-4">
              {invoices.map((invoice) => {
                const createdDate = new Date(invoice.createdAt);
                const isValidDate = !isNaN(createdDate.getTime());
                
                return (
                  <div
                    key={invoice.invoiceId}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-slate-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {isValidDate ? createdDate.toLocaleDateString() : 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {invoice.periodStart && invoice.periodEnd && (() => {
                          const startDate = new Date(invoice.periodStart);
                          const endDate = new Date(invoice.periodEnd);
                          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                            return 'N/A';
                          }
                          return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
                        })()}
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                        {invoice.currency?.toUpperCase() || '$'}{invoice.amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : invoice.status === 'open'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'
                        }`}
                      >
                        {invoice.status}
                      </span>
                      {invoice.stripeHostedInvoiceUrl && (
                        <a
                          href={invoice.stripeHostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                          title="View Invoice"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </SubscriptionLayout>
  );
};

export default InvoicesPage;

